import { namespace } from "@common/config/constants";
import {
    getEffectiveMessageStatus,
    getStatusLabelKey,
    type EffectiveMessageStatus,
} from "@common-components/message-status/message-status-config";
import { useAppDispatch, useAppSelector } from "@common-reducers/hooks/store.hook";
import { editCalendarEventThunk } from "@common-reducers/CalendarThunks";
import { revealCalendarOccurrence } from "@common-reducers/CalendarReducer";
import { isSentRecord } from "@common/services/planner-filters";
import { isScheduleAgainEligible } from "@common/services/schedule-again";
import { ScheduleAgainContext } from "./schedule-again-context";
import { handleMentionsFormatInMessage } from '@common-services/utils';
import { userMessagesSelectOneObjectByQuery } from "@common/reducers/DBServiceThunks";
import { messageDestination } from "@common/components/destination-avatars/destination-plan";
import { sendKindOf } from "@common/components/kind-glyphs/send-kind";
// Same identity palette the calendar grid uses, so one message keeps one
// colour whether you look at it in the list or on the grid.
import { chipColorFor } from "@common/utils/calendar-palette";
import { useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { PausedSeriesControls, usePausedSeriesEntry } from "./paused-series-controls";
import PlannerListRow, { type PlannerRowTone } from "./planner-list-row";

type Props = {
    messageId: string;
    occurrenceDueDate?: string; // For recurring messages, this is the actual occurrence date
}

/** Status tone. `unconfirmed` is deliberately NOT a failure — it went out and
 *  WhatsApp has not confirmed, which for a group send is routine. */
const TONE_BY_STATUS: Partial<Record<EffectiveMessageStatus, PlannerRowTone>> = {
    pending: 'neutral',
    // #576: raw `sent` / `sent_pending_ack` carry no server ack, so they read as
    // `unconfirmed`, never green. (getEffectiveMessageStatus already resolves them;
    // these entries only keep a raw status from painting a false "ok".)
    sent: 'neutral',
    sent_pending_ack: 'neutral',
    confirmed: 'ok',
    received: 'ok',
    read: 'ok',
    unconfirmed: 'neutral',
    in_transit: 'neutral',
    failed: 'bad',
    expired: 'bad',
    rate_limited: 'warn',
};

export default function CalendarSidebarMessageRow({ messageId, occurrenceDueDate }: Props) {
    const { t } = useTranslation(namespace);
    const message = useAppSelector(state => userMessagesSelectOneObjectByQuery(state.DBUserMessageReducer, { _id: messageId }))

    const dispatch = useAppDispatch();
    const scheduleAgain = useContext(ScheduleAgainContext);
    // FE#444 — only a delivered one-off can be run again.
    const canScheduleAgain = !!scheduleAgain && !!message && isScheduleAgainEligible(message);
    const selecting = canScheduleAgain && scheduleAgain.selectMode;

    // The lean calendar event the editor opens from. occurrenceDueDate (when
    // given) pins a recurring message to THIS occurrence.
    const leanEvent = message ? {
        _id: message._id,
        dueDate: occurrenceDueDate ?? message.dueDate,
        isCampaign: false,
        isRecurring: message.isRecurring || false,
        rruleset: message.rruleset,
        // Same marker the grid's chips carry: editCalendarEventThunk refuses a
        // send that already went out (saving it could queue it again).
        ...(isSentRecord(message as any) ? { isSentRecord: true } : {}),
    } : null;

    // A click opens the message in its editor — what the Edit item of the old
    // Edit/Delete menu did, without the menu in between (same as the calendar
    // grid). Delete lives in the editor. A send that already went out is a
    // record, not a plan, so it is SHOWN instead: the calendar moves to it and
    // flashes its chip, whose card has the details.
    const handleRowClick = (e: React.MouseEvent) => {
        // In select mode a click on a selectable row ticks it instead of opening it.
        if (selecting) {
            scheduleAgain.toggle(messageId);
            return;
        }
        // Only trigger if it's a left click
        if (e.button !== 0) return;
        if (!message || !leanEvent) return;

        if ((leanEvent as { isSentRecord?: boolean }).isSentRecord) {
            dispatch(revealCalendarOccurrence({ id: String(leanEvent._id), isCampaign: false, dueDate: new Date(leanEvent.dueDate as any).toISOString() }));
            return;
        }
        dispatch(editCalendarEventThunk(leanEvent as any));
    };

    const paused = usePausedSeriesEntry(messageId);
    const effective = getEffectiveMessageStatus(message);
    const contact = message?.contactList?.[0];
    // A Status is recognised by the recipient the send thunk keys on — through
    // the same `sendKindOf` the calendar chip uses, so the two cannot disagree.
    // Its row names the destination in the reader's language ("My Status")
    // instead of the English name stored at compose time or the raw
    // `status@broadcast`, and its circle is the account's own Status — never a
    // contact avatar, which would try to resolve a picture for the broadcast id.
    const kind = sendKindOf(message ? { isPoll: message.isPoll, contactList: message.contactList as any } : null);
    const isStatus = kind === 'status';
    const destination = useMemo(() => messageDestination(message as any), [message]);
    const destinationHints = useMemo(() => Object.fromEntries(
        (message?.contactList ?? []).map((c: any) => [String(c?.id ?? ''), { name: c?.name, picture: c?.profilePicUrl }]),
    ), [message]);
    // Several recipients is a fact about the send, so it belongs on the row —
    // the old box showed only the first name and silently dropped the rest.
    const extraRecipients = isStatus ? 0 : Math.max((message?.contactList?.length ?? 1) - 1, 0);

    return (
                    <PlannerListRow
                        railColor={chipColorFor(messageId)}
                        kind={kind}
                        // Same avatar resolution as the contact picker: the id
                        // first (the cache knows the picture), the URL stored on
                        // the message only as a hint — both via ContactAvatar.
                        destination={destination}
                        destinationHints={destinationHints}
                        title={isStatus
                            ? t('scheduledMessage.status.myStatus')
                            : contact?.name || message?.contactWAId || t('calendar.grid.untitled', 'Message')}
                        preview={handleMentionsFormatInMessage(message?.message ?? '')}
                        detail={extraRecipients > 0
                            ? t('scheduledMessages.plusMore', { count: extraRecipients, defaultValue: `+${extraRecipients} more` })
                            : undefined}
                        dueDate={occurrenceDueDate ?? (message?.dueDate as any)}
                        isRecurring={message?.isRecurring}
                        status={paused
                            ? { label: t('pausedSeries.badge'), tone: 'warn' }
                            : effective
                                ? { label: t(getStatusLabelKey(message)), tone: TONE_BY_STATUS[effective] ?? 'neutral' }
                                : undefined}
                        extra={paused ? <PausedSeriesControls entry={paused} /> : undefined}
                        onClick={handleRowClick}
                        selection={selecting ? { checked: scheduleAgain.selected.has(messageId), onToggle: () => scheduleAgain.toggle(messageId) } : undefined}
                        onScheduleAgain={canScheduleAgain && !selecting ? () => scheduleAgain.openFor(messageId) : undefined}
                        // THIS occurrence (a recurring message has one chip per date).
                        onReveal={leanEvent?.dueDate
                            ? () => dispatch(revealCalendarOccurrence({ id: String(leanEvent._id), isCampaign: false, dueDate: new Date(leanEvent.dueDate as any).toISOString() }))
                            : undefined}
                    />
    )
}
